import React, { useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { Provider } from 'react-redux';
import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from '@redux-devtools/extension';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';

import rootReducer from '../../reducers/index';
import { getConfig } from '../../utils/api';
import logger from '../../utils/logger';
import { theme } from '../../utils/theme';
import ErrorBoundary from '../ErrorBoundary';
import { SidePanelProvider } from '../SidePanel/store';

// Create Emotion cache with custom key prefix to avoid CSS collisions
// when gradebook is used as a microfrontend alongside other MUI apps
const emotionCache = createCache({
  key: 'ml-rni',
  prepend: true, // Ensures MUI styles are loaded first
});

const getApiHost = (clientHost, apiHost) => {
  if (apiHost) return apiHost;

  if (import.meta.env.VITE_APP_GRADEBOOK_API_URL) {
    return import.meta.env.VITE_APP_GRADEBOOK_API_URL;
  }

  // use clientHost if embeded as a micro frontend
  const hostname = clientHost || window.location.hostname;

  // For local development - derive domain from current hostname
  if (hostname.includes('local')) {
    const domain = hostname.split('.').slice(1).join('.');
    return `https://gradebook-service-local.${domain}:10000`;
  }

  // prod and int uses -client (gradebook-client → gradebook-api)
  // other envs use -frontend (gradebook-api-frontend → gradebook-api)
  const resolvedHost = hostname
    .replace('-client', '-api')
    .replace('-frontend', '');
  return hostname.includes('https') ? resolvedHost : `https://${resolvedHost}`;
};

const getCoursewareApiHost = (clientHost, apiHost) => {
  if (apiHost) return apiHost;

  if (import.meta.env.VITE_APP_COURSEWARE_API_URL) {
    return import.meta.env.VITE_APP_COURSEWARE_API_URL;
  }

  // use clientHost if embeded as a micro frontend
  const hostname = clientHost || window.location.hostname;

  // For local development - derive domain from current hostname
  if (hostname.includes('local')) {
    const domain = hostname.split('.').slice(1).join('.');
    return `https://courseware-service-local.${domain}`;
  }

  return hostname.includes('https')
    ? hostname.replace('-frontend', '')
    : `https://${hostname.replace('-frontend', '')}`;
};

const queryClient = new QueryClient();

export default function Wrapper({ children, profilerId, ...externalProps }) {
  const { clientHost, apiHost, disableEnforceFocus = false } = externalProps;
  const _apiHost = getApiHost(clientHost, apiHost);
  window.config = window.config || {
    GRADEBOOK_API_URL: _apiHost,
    COURSEWARE_API_URL: getCoursewareApiHost(clientHost, apiHost),
  };

  const [config, setConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [store, setStore] = useState(null);

  useEffect(() => {
    // Inject Figtree font if not already present
    const figtreeFontId = 'gradebook-figtree-font';
    if (!document.getElementById(figtreeFontId)) {
      const link = document.createElement('link');
      link.id = figtreeFontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap';
      document.head.appendChild(link);
    }

    // Inject Source Sans Pro font if not already present
    const sourceSansFontId = 'gradebook-source-sans-pro-font';
    if (!document.getElementById(sourceSansFontId)) {
      const link = document.createElement('link');
      link.id = sourceSansFontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@300;400;600;700;900&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    // Create redux store upon mount
    const store = createStore(
      rootReducer,
      composeWithDevTools(applyMiddleware(thunk)),
    );

    if (import.meta.hot) {
      // Enable vite hot module replacement for reducers
      import.meta.hot.accept('../../reducers', () => {
        const nextRootReducer = require('../../reducers/index');
        store.replaceReducer(nextRootReducer);
      });
    }
    setStore(store);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (_apiHost) {
        try {
          // This will prevent the widget from completely reloading
          // if the user just selected a specific student and
          // instead show an overlay over the input and table
          setIsConfigLoading(true);

          const data = await getConfig(_apiHost);

          if (data && !data.error && !isEmpty(data)) {
            setConfig(data);
          } else {
            throw new Error(data.error);
          }
        } catch (error) {
          console.log(error);
          logger(error);
        } finally {
          setIsConfigLoading(false);
        }
      }
    };
    fetchData();
  }, [_apiHost]);

  if (store && config) {
    // Generate class name for scoping styles (e.g., "gradebook-student" or "gradebook-instructor")
    const scopeClass = profilerId
      ? `gradebook-${profilerId.toLowerCase().replace('gradebook', '')}`
      : 'gradebook';

    return (
      <div className={scopeClass}>
        <CacheProvider value={emotionCache}>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                  <SidePanelProvider>
                    <ErrorBoundary clientHost={clientHost}>
                      {React.cloneElement(children, {
                        ...externalProps,
                        isConfigLoading,
                        config,
                        apiHost: _apiHost,
                        disableEnforceFocus,
                      })}
                    </ErrorBoundary>
                  </SidePanelProvider>
                </ThemeProvider>
              </StyledEngineProvider>
            </Provider>
          </QueryClientProvider>
        </CacheProvider>
      </div>
    );
  }
  return null;
}
